import React, { useRef } from 'react';
import { DataGridPro, GridColDef } from '@mui/x-data-grid-pro';
import { Box, Typography, Button, IconButton } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import CancelIcon from "@mui/icons-material/Cancel";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import CheckIcon from "@mui/icons-material/Check";
import { EnrichedSocialContentPublication, SocialPlatformNames } from "../../../types/socialTypes";

import { ButtonWithPopover, ButtonWithPopoverRef } from "../CommonComponents/ButtonWithPopover";

interface PublicationHistoryProps {
    rows: EnrichedSocialContentPublication[];
    onPublicationDelete?: (socialPostId: string) => void;
}

// Sotto-componente per gestire lo stato della singola cella di eliminazione
const DeleteActionCell: React.FC<{ onDelete: () => void }> = ({ onDelete }) => {
    const deletePopoverRef = useRef<ButtonWithPopoverRef>(null);

    const handleConfirmDelete = () => {
        onDelete();
        deletePopoverRef.current?.close();
    };

    const handleCancelDelete = () => {
        deletePopoverRef.current?.close();
    };

    const deletePopoverContent = (
        <Box display="flex" flexDirection="column" gap={1.5} minWidth="200px">
            <Typography variant="body2" color="text.primary">
                Are you sure you want to delete this publication?
            </Typography>
            <Box display="flex" justifyContent="center" gap={1}>
                <Button
                    startIcon={<CancelIcon/>}
                    size="small"
                    variant="contained"
                    onClick={handleCancelDelete}
                >
                    Cancel
                </Button>
                <Button
                    startIcon={<DeleteIcon/>}
                    size="small"
                    variant="contained"
                    onClick={handleConfirmDelete}
                >
                    Confirm
                </Button>
            </Box>
        </Box>
    );

    return (
        <ButtonWithPopover
            ref={deletePopoverRef}
            variant="text"
            color="error"
            popoverContent={deletePopoverContent}
            sx={{ minWidth: 0, p: 1, borderRadius: '50%' }}
        >
            <DeleteIcon />
        </ButtonWithPopover>
    );
};

export const PublicationHistory: React.FC<PublicationHistoryProps> = ({ rows, onPublicationDelete = () => {}}) => {

    const columns: GridColDef[] = [
        { field: 'social_content_name', headerName: 'Type', width: 70 },
        { field: 'social_account_name', headerName: 'Account', width: 200 },
        { field: 'social_platform_name', headerName: 'Platform', width: 100 },
        { field: 'status', headerName: 'Status', width: 100 },
        {
            field: 'published_at',
            headerName: 'Published At',
            width: 125,
            renderCell: (params) => {
                const dateVal = params.row.published_at;

                // Se la data non c'è, è vuota, o include "1970" (il default Epoch), mostriamo un placeholder
                if (!dateVal || dateVal.includes('1970')) {
                    return (
                        <Box display="flex" alignItems="center" height="100%">
                            <Typography variant="body2" color="text.secondary">

                            </Typography>
                        </Box>
                    );
                }

                return dateVal;
            }
        },
        { field: 'aspect_ratio', headerName: 'Aspect Ratio', width: 100 },
        { field: 'like', headerName: 'Likes', width: 100 },
        { field: 'view', headerName: 'Views', width: 100 },
        { field: 'comment', headerName: 'Comments', width: 100 },
        { field: 'share', headerName: 'Shares', width: 100 },
        { field: 'save', headerName: 'Save', width: 100 },
        {
            field: 'materialization_available',
            headerName: 'Available',
            width: 75,
            renderCell: (params) => (
                <Box width={'100%'} display={'flex'} justifyContent={'center'} alignItems={'center'} height={'100%'}>
                    {params.row.materialization_available && <CheckIcon color="success" />}
                </Box>
            ),
        },
        {
            field: 'actions',
            headerName: '',
            flex: 1,
            sortable: false,
            filterable: false,
            disableColumnMenu: true,
            renderCell: (params) => (
                <Box width={'100%'} display={'flex'} justifyContent={'center'} alignItems={'center'} height={'100%'} gap={1}>
                    {params.row.publication_url && (
                        <IconButton
                            size="small"
                            color="primary"
                            href={params.row.publication_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="Apri post"
                        >
                            <OpenInNewIcon fontSize="small" />
                        </IconButton>
                    )}
                    {params.row.social_platform_name !== SocialPlatformNames.instagram && (
                        <DeleteActionCell
                            onDelete={() => onPublicationDelete(params.row.content_publication_id)}
                        />
                    )}
                </Box>
            ),
        },
    ];

    return (
        <Box sx={{ height: 600, width: '100%' }}>
            <DataGridPro
                getRowId={(row) => row.content_publication_id}
                rows={rows}
                columns={columns}
                pageSizeOptions={[10, 15, 20, 25, 30]}
                disableRowSelectionOnClick
            />
        </Box>
    );
};